import { DialogActions, DialogContent, TextField } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { parseErrorMessage } from '../../../../../../core/api/api.context';
import { Modal } from '../../../../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../../../../ui/components/pure/text-button/text-button.component';
import { dialogActionsStyle } from '../../../../components/courier-form.styles';
import { COURIER_BATCH_FIELD_LABELS, COURIER_BATCH_TEMPLATE_FIELDS } from '../../../../logic/courier-batch.map';
import {
  BatchImportCouriersActionHandlers,
  BatchImportCouriersActionState
} from '../batch-import-couriers-action.hook';

const PASTE_FIELD_ID = 'courier-batch-paste';

// Shown in the empty field, so the operator can see the shape the rows should take.
const PASTE_PLACEHOLDER = [
  COURIER_BATCH_TEMPLATE_FIELDS.map((field) => COURIER_BATCH_FIELD_LABELS[field]).join(','),
  'Kaye,Totleben,,+61400000000,DRV001,,,,'
].join('\n');

// The columns a row must fill in to import, named as the template's headings name them.
const label = COURIER_BATCH_FIELD_LABELS;
const REQUIRED_FIELDS = [
  label.firstName,
  `${label.mobile}, ${label.externalID}, or ${label.username} and ${label.accessCode}`
];

/** The first step: a template to fill in, and the completed file or pasted CSV to read back. */
export function CourierBatchInputModal({
  state,
  handlers,
  loading
}: {
  state: BatchImportCouriersActionState;
  handlers: BatchImportCouriersActionHandlers;
  loading: boolean;
}) {
  const hasInput = !!state.fileContent || state.pastedText.trim() !== '';

  return (
    <Modal title="Batch import" open={true} loading={loading} maxWidth="md" onClose={handlers.onCancel}>
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        {state.error && (
          <div style={{ margin: '16px 0' }}>
            <Alert variant="outlined" severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
              {parseErrorMessage(state.error.message)}
            </Alert>
          </div>
        )}
        Please download and complete the template spreadsheet below. CSV files can be edited in Microsoft Excel or
        Google Sheets.
        <div style={{ paddingTop: '8px' }}>Each courier needs:</div>
        <ul style={{ margin: '4px 0 0', paddingLeft: '24px' }}>
          {REQUIRED_FIELDS.map((requirement) => (
            <li key={requirement}>{requirement}</li>
          ))}
        </ul>
        <div style={{ paddingTop: '8px' }}>
          <TextButton color="primary" variant="outlined" onClick={handlers.onDownloadTemplate}>
            Download CSV Template
          </TextButton>
        </div>
        <div style={{ margin: '16px 0' }}>
          Then upload the completed CSV file here.
          <div style={{ paddingTop: '8px' }}>
            <TextButton color="primary" variant="outlined" onClick={handlers.onUploadFile}>
              Upload CSV
            </TextButton>
          </div>
          {state.isFileUploadFailure && (
            <div style={{ paddingTop: '8px' }}>
              <Alert variant="outlined" severity="error">
                <AlertTitle>There was an error uploading your file. Please try again.</AlertTitle>
              </Alert>
            </div>
          )}
          {state.fileName && (
            <div style={{ paddingTop: '8px' }}>
              <Alert variant="outlined" severity="info">
                <AlertTitle>File uploaded: {state.fileName}</AlertTitle>
              </Alert>
            </div>
          )}
        </div>
        <div style={{ padding: '4px 0 8px' }}>Or paste the rows here instead, with the heading row first.</div>
        <TextField
          id={PASTE_FIELD_ID}
          variant="outlined"
          value={state.pastedText}
          onChange={(event) => handlers.onPasteChange(event.target.value)}
          placeholder={PASTE_PLACEHOLDER}
          multiline
          minRows={18}
          maxRows={18}
          fullWidth
          slotProps={{
            htmlInput: { spellCheck: false, style: { fontFamily: 'monospace', fontSize: '13px', lineHeight: 1.5 } }
          }}
        />
      </DialogContent>
      <DialogActions style={dialogActionsStyle}>
        <TextButton color="secondary" variant="outlined" onClick={handlers.onCancel}>
          Cancel
        </TextButton>
        <TextButton color="primary" variant="contained" onClick={handlers.onContinue} disabled={!hasInput || loading}>
          Continue
        </TextButton>
      </DialogActions>
    </Modal>
  );
}
